/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import Rails from "@rails/ujs";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { aws_presign_path } from "routes";

export class ApiError extends Error {
  constructor(message: string, json) {
    super(message);
    this.name = this.constructor.name;
    this.json = json;
  }

  all(): Array {
    if (Array.isArray(this.json)) {
      return this.json;
    } else {
      return [this.json];
    }
  }
}

axios.defaults.headers.common = {
  Accept: "application/json",
  "Content-Type": "application/json",
  "X-CSRF-Token": Rails.csrfToken()
};
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    const { data } = response;

    throw new ApiError(response.statusText, data);
  }
);

const request = async <T = unknown, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> => {
  return await axios.create(config);
};

const get = async <T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> => {
  return await axios.get<T, R>(url, config);
};

const post = async <T = unknown, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> => {
  return await axios.post<T, R>(url, data, config);
};

const put = async <T = unknown, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> => {
  return await axios.put<T, R>(url, data, config);
};

const deleteItem = async <T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> => {
  return await axios.delete<T, R>(url, config);
};

const upload = async <TResponseData>(fileList: FileList): Promise<TResponseData> => {
  if (fileList?.length == 0) {
    throw new Error("No file in file list argument.");
  }

  const file = fileList[0];

  let directory = "production";

  try {
    if (process.env.NODE_ENV == "development") {
      directory = "staging";
    }

    const { get_url, post_url } = await get(aws_presign_path({ file_name: file.name, directory: directory }));
    const options = {
      headers: { "Content-Type": file.type, acl: "public-read" }
    };

    await axios.put(post_url, file, options);

    return get_url;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const api = {
  request,
  get,
  post,
  put,
  deleteItem,
  upload
};
